import { useState, useEffect, type ReactNode } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { NotificationBell } from '@/components/ui/NotificationBell';

interface NavItem {
  label: string;
  to: string;
  icon: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', to: '/', icon: 'grid' },
  { label: 'Portfolio', to: '/portfolio', icon: 'portfolio' },
  { label: 'New Idea', to: '/new', icon: 'plus' },
];

const bottomNavItems: NavItem[] = [
  { label: 'Settings', to: '/admin', icon: 'settings', adminOnly: true },
];

const iconMap: Record<string, ReactNode> = {
  grid: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  plus: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  settings: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  user: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  logout: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16,17 21,12 16,7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  portfolio: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  collapse: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
};

function NavItemLink({ item, collapsed, onClick }: { item: NavItem; collapsed: boolean; onClick?: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      title={collapsed ? item.label : undefined}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          collapsed ? 'justify-center' : ''
        } ${
          isActive
            ? 'bg-teal/10 text-teal'
            : 'text-text-secondary hover:bg-surface hover:text-text-primary'
        }`
      }
    >
      {iconMap[item.icon]}
      {!collapsed && item.label}
    </NavLink>
  );
}

function SidebarContent({ collapsed, setCollapsed, onNavigate }: {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  onNavigate?: () => void;
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.systemRoles?.includes('super_admin') || user?.systemRoles?.includes('admin');
  const avatarUrl = user?.avatar_url;

  return (
    <>
      {/* Logo + collapse toggle */}
      <div className={`hidden md:flex items-center border-b border-border px-3 py-3 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        <button onClick={() => navigate('/')} className="shrink-0" title="Go to dashboard">
          {collapsed ? (
            <img src="/captive_path_icon_black.png" alt="Captive Path" className="h-6 w-6" />
          ) : (
            <img src="/captive_path_logo.webp" alt="Captive Path" className="h-6" />
          )}
        </button>
        {!collapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="rounded-md p-1.5 text-text-muted hover:text-text-primary hover:bg-surface transition-colors"
            title="Collapse sidebar"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <polyline points="15,18 9,12 15,6" />
            </svg>
          </button>
        )}
      </div>

      {/* Expand button when collapsed */}
      {collapsed && (
        <div className="hidden md:flex items-center justify-center p-2">
          <button
            onClick={() => setCollapsed(false)}
            className="rounded-md p-1.5 text-text-muted hover:text-text-primary hover:bg-surface transition-colors"
            title="Expand sidebar"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <polyline points="9,18 15,12 9,6" />
            </svg>
          </button>
        </div>
      )}

      {/* Main navigation */}
      <nav className="flex flex-col gap-0.5 px-3 mt-2 md:mt-2">
        {navItems.map((item) => (
          <NavItemLink key={item.to} item={item} collapsed={collapsed} onClick={onNavigate} />
        ))}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Bottom section: Admin + Notifications + User Profile + Logout */}
      <div className="flex flex-col gap-0.5 border-t border-border px-3 py-3">
        {bottomNavItems
          .filter((item) => !item.adminOnly || isAdmin)
          .map((item) => (
            <NavItemLink key={item.to} item={item} collapsed={collapsed} onClick={onNavigate} />
          ))}

        {/* Notifications */}
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2.5'} rounded-md px-3 py-2`}>
          <NotificationBell />
          {!collapsed && <span className="text-sm font-medium text-text-secondary">Notifications</span>}
        </div>

        {user && (
          <button
            onClick={() => { navigate('/profile'); onNavigate?.(); }}
            title={collapsed ? `${user.name} — Profile` : 'Profile settings'}
            className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-left hover:bg-surface transition-colors ${collapsed ? 'justify-center' : ''}`}
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-teal/10 text-xs font-semibold text-teal">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                user.name?.charAt(0).toUpperCase() || 'U'
              )}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-text-primary truncate">{user.name}</p>
                <p className="text-[10px] text-text-muted truncate">{user.email}</p>
              </div>
            )}
          </button>
        )}

        <button
          onClick={() => { logout(); onNavigate?.(); }}
          title={collapsed ? 'Sign out' : undefined}
          className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface hover:text-text-primary transition-colors ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          {iconMap.logout}
          {!collapsed && 'Sign out'}
        </button>
      </div>
    </>
  );
}

export function Sidebar() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === 'true'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('sidebar-collapsed', String(collapsed)); } catch { /* noop */ }
  }, [collapsed]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex shrink-0 flex-col border-r border-border bg-surface transition-all duration-normal ${collapsed ? 'w-16' : 'w-56'}`}>
        <SidebarContent collapsed={collapsed} setCollapsed={setCollapsed} />
      </aside>

      {/* Mobile overlay — full-width */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <aside
            className="relative flex h-full w-full flex-col bg-surface shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <button onClick={() => { setMobileOpen(false); }} title="Go to dashboard">
                <img src="/captive_path_logo.webp" alt="Captive Path" className="h-6" />
              </button>
              <button
                onClick={() => setMobileOpen(false)}
                className="rounded-md p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-alt transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <SidebarContent collapsed={false} setCollapsed={setCollapsed} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Mobile hamburger button */}
      <MobileMenuButton open={mobileOpen} onToggle={() => setMobileOpen(!mobileOpen)} />
    </>
  );
}

export function MobileMenuButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const navigate = useNavigate();
  if (open) return null;
  return (
    <div className="flex md:hidden items-center justify-between w-full border-b border-border bg-surface px-4 py-2.5 shrink-0">
      <button onClick={() => navigate('/')} className="shrink-0" title="Home">
        <img src="/captive_path_logo.webp" alt="Captive Path" className="h-6" />
      </button>
      <button
        onClick={onToggle}
        className="rounded-md p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-alt transition-colors"
        aria-label="Open menu"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
    </div>
  );
}
